const post = {
  slug: 'kubernetes-wont-fix-your-architecture',
  number: 4,
  title: "Kubernetes Won't Fix Your Architecture",
  date: '2026-06-25',
  readTime: '8 min read',
  views: 118,
  category: 'devops',
  tags: ['Kubernetes', 'Platform', 'DevOps'],
  excerpt:
    'Teams adopt Kubernetes hoping it will fix slow deploys and flaky environments. It will not. It gives you good tools to manage those problems, but only once you understand what you are running.',
  cover: null,
  content: `
When a team adopts Kubernetes, there is usually a quiet hope riding along with it: that this will fix everything. The slow deploys, the flaky environments, the scaling headaches, all of it solved by the platform everyone says is the answer. I have watched that hope meet reality more than once, and the reality is that Kubernetes does not fix your problems. It gives you a consistent, powerful set of tools to manage them, and it hands you a pile of new complexity in the same motion. Whether that is a good trade depends entirely on whether you understand what you are actually running.

Here are the things I wish someone had sat me down and explained before my first cluster, rather than letting me learn them from incidents.

## Set resource requests and limits, always

The most common cause of a mysterious production problem I have seen on Kubernetes is missing resource requests. Without them, the scheduler is guessing. Pods land on nodes that cannot really hold them, and everything gets evicted under load at the worst possible moment, usually during the traffic spike you built the cluster to handle.

\`\`\`yaml
resources:
  requests:
    cpu: "250m"
    memory: "256Mi"
  limits:
    cpu: "500m"
    memory: "512Mi"
\`\`\`

Set these for every workload. Start conservative, watch the real usage in Grafana, then tune. The numbers you guess on day one will be wrong, and that is fine, because now you can see them and adjust instead of finding out during an outage. One subtlety worth knowing: a pod that hits its memory limit does not get throttled, it gets killed. Set memory limits with that in mind, because the failure mode is abrupt.

## Health checks are not optional

Kubernetes can only keep your app healthy if it knows what healthy means. A liveness probe tells it when to restart a stuck pod. A readiness probe tells it when a pod is ready to take traffic. Get the readiness probe wrong and you send requests to a pod that is still warming up, then spend an afternoon wondering why the first few requests after every deploy fail.

\`\`\`yaml
readinessProbe:
  httpGet:
    path: /healthz
    port: 8080
  initialDelaySeconds: 5
  periodSeconds: 10
\`\`\`

A mistake I made early was pointing the liveness probe at an endpoint that also checked the database. When the database had a brief hiccup, every pod failed its liveness check at once and Kubernetes dutifully restarted all of them, turning a small blip into a full outage. Liveness should answer one question, "is this process wedged?", and nothing more. Save the dependency checks for readiness.

## Most of my outages were config, not code

Looking back at the incidents I have actually dealt with, very few were caused by the container image itself. They were a wrong environment variable, a secret that did not get mounted, a config map that quietly drifted between staging and production. The code was fine. The configuration around it was not.

So treat your manifests with the same seriousness as your application code. Review them, version them, and keep your environments as close to identical as you can. This is where tools like Helm or Kustomize earn their place, not because templating is exciting, but because they let you keep one source of truth and change only the few things that genuinely differ between environments, instead of maintaining three hand-edited copies that slowly diverge into incidents.

## Do not reach for it too early

Kubernetes has real operational weight. Someone has to own the cluster, the upgrades, the networking, the security patches, the certificate rotation. For a small service with predictable load, a managed container platform or even a couple of plain virtual machines will serve you better and cost you a fraction of the attention.

Reach for Kubernetes when you actually have the problems it solves: many services, real scaling needs, a team that benefits from a shared deployment model. Adopting it before then means you have taken on all of the complexity and almost none of the payoff, and your engineers spend their days operating a cluster instead of building the product.

## A short checklist

Before I trust a workload on Kubernetes:

- Resource requests and limits set on every container, tuned against real usage.
- Liveness probes that only check the process, readiness probes that gate traffic.
- Manifests reviewed and versioned like code, with environments kept in sync.
- A clear owner for the cluster itself: upgrades, patching, networking.
- An honest answer to "do we actually need this, or does something simpler fit?"

Kubernetes rewards teams that respect it and punishes the ones that treat it as a magic box. It is a genuinely powerful tool. It is not a substitute for understanding your own system, and no amount of YAML will paper over an architecture you have not thought through.
`,
};

export default post;
